// src/components/UserMenu.jsx
import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME, APP_VERSION } from '../config/appConfig';
import { changeLanguage, SUPPORTED_LANGUAGES } from '../i18n';

const UserMenu = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [langOpen, setLangOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="w-9 h-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label={t('header.openMenu')}
      >
        <i className="fas fa-bars text-xl"></i>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-lg shadow-lg border border-gray-200 z-50 focus:outline-none">
          <div className="px-4 py-3 flex items-center gap-3">
            <i className="fas fa-user-circle text-gray-500 text-xl w-5 text-center"></i>
            <span className="text-sm font-medium text-gray-800 truncate">
              {user?.username || '—'}
            </span>
          </div>

          <div className="border-t border-gray-200"></div>

          <button
            type="button"
            onClick={() => setLangOpen(prev => !prev)}
            className="w-full px-4 py-2.5 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50"
          >
            <span className="flex items-center gap-3">
              <i className="fas fa-language text-gray-500 w-4 text-center"></i>
              {t('header.language')}
            </span>
            <span className="flex items-center gap-1 text-gray-500">
              {t(currentLang.labelKey)}
              <i className={`fas fa-caret-down text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
            </span>
          </button>

          {langOpen && (
            <div className="border-t border-gray-100 bg-gray-50">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    changeLanguage(lang.code);
                    setLangOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 pl-11 text-sm hover:bg-gray-100 ${
                    i18n.language === lang.code ? 'text-purple-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  {t(lang.labelKey)}
                </button>
              ))}
            </div>
          )}

          <div className="px-4 py-4 mt-2 text-center text-xs text-gray-400">
            {APP_NAME} v{APP_VERSION}
          </div>

          <div className="border-t border-gray-200"></div>

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={handleLogout}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${
                  active ? 'bg-gray-50' : 'text-gray-700'
                }`}
              >
                <i className="fas fa-sign-out-alt text-gray-500 w-4 text-center"></i>
                {t('nav.logout')}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default UserMenu;
